// workspaceLayout — Modern Layout の「構造化レイアウト state」とその変換 (presentation 層・純関数).
//
// 背景 (docs/_wip/modern-layout.md §9.10 ユーザーカスタム配置 + ワークスペース preset、
// consult 2026-06-14 / 2026-06-16):
//   - ModernLayout.tsx は元々 9 パネルの位置・順序・幅を **JSX 直書き + 固定 Tailwind**
//     でハードコードしていた。第1スライス (consult 2026-06-14) で「region 順 + region →
//     パネル id」の構造化 state へ寄せ、左右ミラーを order 反転の純変換として実装した。
//   - 第2スライス (consult 2026-06-16、本ファイル) で §9.10 を Core まで進める:
//     **(2) state を visibility / 畳み / ドック幅まで拡張** (§9.10 決定2「同一 1 本の
//     state を操作」)、**(3) mirror フラグ → 名前付き Tier1 preset の一般化**
//     (`resolveWorkspace(presetId)`)、**(4) curated な組込 preset を厳選**。
//   - §9.10 決定2 (二重管理回避): B (preset/ミラー) も C (手動編集) も **同一の1本の
//     レイアウト state** を操作する。**preset = この state のスナップショットに名前を
//     付けたもの** (= 決定3)。手動編集 (Optional) も同じ state を直接いじる。
//
// ## モデル: region (列) のリスト + region → panel ids + 表示/畳み/幅
//   ModernLayout の骨格 (§9 案B 配置調整 2026-06-08) は canvas を中心に左右へ「列
//   (region)」が並ぶ:
//       [ 左 aside ........ ] [ canvas ] [ 右 aside ]
//         ├ leftRail カラム               └ rightDock (プレビュー→レイヤー→社交)
//         └ toolColumn カラム (プロパティ→カラー→16色)
//   これを **region の左→右の並び (`order`)** と **各 region に入るパネル id の縦並び
//   (`panels`)** + **パネルごとの表示フラグ (`hidden`)** + **region ごとのドック幅
//   (`widths`)** で表す。
//
// ## ミラー = order の反転 (左右入替) — preset の中の 1 つ `mirror`
//   左右ミラー (§9.10「UI左右入替」) = **order を反転**する純変換。canvas は order 上で
//   対称な位置なので反転しても中央に残る (前後の region 数が同じため)。
//   注意: これは **レイアウト左右反転 (パネル列の入替)**。ModernToolRail の「左右反転」
//   ボタン (= キャンバスの描画ミラー、canvas-flip.md) とは **完全に別機能**。
//
// ## なぜ「配置だけ」をデータ化し render はレジストリに残すか (純化の方針)
//   §9.10 設計論点: パネルによっては ModernLayout のローカル state に依存した手配線
//   (recentColors 連携等) がある。これを無理に「純粋な id→component map」へ落としきらない。
//   このモジュールは **配置 (どの region に・どの順・表示・幅)** だけをデータ化し、各
//   パネルの render は ModernLayout 側の `panelRegistry` (= クロージャ) が持つ。

/** パネルが入る「列 (region)」。§9 案B 配置調整 (2026-06-08) の 4 region。 */
export type WorkspaceRegion =
  /** 左 aside 内側カラム1: ツール帯 + コマンドクラスタ。 */
  | 'leftRail'
  /** 左 aside 内側カラム2: ツールプロパティ → カラー → 16色/最近色。 */
  | 'toolColumn'
  /** 中央 canvas 空スロット (§9.7 計測フローティング・スロット)。 */
  | 'canvas'
  /** 中右 1 本ドック: プレビュー → レイヤー → 社交。 */
  | 'rightDock';

/** パネル (= 再配置可能ユニット) の安定 id。render は ModernLayout 側レジストリが持つ。 */
export type PanelId =
  | 'toolRail'
  | 'commandCluster'
  | 'brushPanel'
  | 'toolProperties'
  | 'colorPanel'
  | 'colorPalette'
  | 'canvas'
  | 'previewPanel'
  | 'layerPanel'
  | 'socialDock';

/** 全 PanelId の網羅リスト (validate / 既定 visibility 生成に使う安定列)。 */
export const ALL_PANEL_IDS: ReadonlyArray<PanelId> = [
  'toolRail',
  'commandCluster',
  'brushPanel',
  'toolProperties',
  'colorPanel',
  'colorPalette',
  'canvas',
  'previewPanel',
  'layerPanel',
  'socialDock',
];

/**
 * leftRail (= 端ドック専用レール) を構成するパネル群。toolRail (ツール帯) と commandCluster
 * (コマンドクラスタ) の細アイコン列だけ。**これら以外のパネル (= wide パネル) は leftRail に
 * 入れられない** (= leftRail はレール専用の閉じたユニット。region 単位で 4 スロット移動し、
 * 常にスリム hug 幅を保つ。第5スライス)。movePanel / validateWorkspace の不変条件に使う。
 */
export const RAIL_PANEL_IDS: ReadonlySet<PanelId> = new Set<PanelId>([
  'toolRail',
  'commandCluster',
]);

/** パネルが leftRail 専用のレールパネル (toolRail / commandCluster) か。 */
export const isRailPanel = (id: PanelId): boolean => RAIL_PANEL_IDS.has(id);

/**
 * フロート中パネルの画面位置 (px, viewport 座標系)。S2 (floating-panels-2026-08-01.md
 * §6 決定4) で `WorkspaceState.floating` の値として使う。
 */
export interface FloatPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * 構造化レイアウト state (§9.10 決定2「同一の1本の state」)。第2スライスで配置に加え
 * **表示/畳み (visibility)** と **ドック幅 (widths)** を持つ (= preset/手動編集の単一土台)。
 * S2 (floating-panels-2026-08-01.md §6 決定4) で **`floating` (フロート配置)** を追加。
 *
 * - `order`: region の左→右の並び (= DOM 配置順)。canvas を含む。
 * - `panels`: region → そのスロットに入るパネル id の縦並び (順序が表示順)。**フロート中の
 *   パネルはどの region の配列にも現れない** (= docked と floating は排他。floatPanel が
 *   region 配列から取り除き、movePanel が re-dock 時に取り除く)。
 * - `hidden`: パネル id → 非表示か (= 畳み/隠し)。**省略 (undefined) = 表示**。
 *   `canvas` は常に表示 (= §9.7 計測スロットを壊さない、validate で強制)。フロート中パネルにも
 *   適用できる (= 非表示のまま位置だけ記憶、パネル表示メニューで復帰可能)。
 * - `widths`: aside を構成する region (leftRail / toolColumn / rightDock) → ドック幅 px。
 *   **省略 = レイアウト既定幅** (toolColumn を含む左群 = 244 / rightDock を含む右群 = 260)。
 *   ModernLayout は aside (= canvas で分割した region 群) の幅を、その群が含む region の
 *   width 指定 (なければ既定) から決める。
 * - `floating`: パネル id → フロート位置 (px)。**キーが存在する = そのパネルはフロート状態**
 *   (= §6 決定4「`docked(region, index)` | `floating(x, y)` の2状態」の floating 側。docked
 *   側は従来通り `panels[region]` 内の位置で表す、別の型タグは導入しない)。省略/存在しないキー
 *   = docked。`canvas` はフロート不可 (§5 スコープ)。`RAIL_PANEL_IDS` (toolRail/commandCluster)
 *   も**個別には**フロート不可 — §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01)
 *   で leftRail 全体が**一体のユニットとして**フロートできるようになったため、rail パネルの
 *   フロートは下記 `railFloating` が専管する (`floating` に rail パネルの id が入ることは
 *   不変条件違反、validateWorkspace で検出)。
 * - `railFloating`: leftRail (toolRail+commandCluster 一体、旧 Java 版 `/open tool` パリティの
 *   Modern 版) がフロート中の画面位置。**キーが存在する = leftRail 全体がフロート中**であり、
 *   このとき `order` から `'leftRail'` は取り除かれる (= docked/floating の排他は通常パネルと
 *   同じ設計、§6 決定4 の region 版)。§5 のスコープ表で「toolRail/commandCluster はフロート
 *   対象外」としていたが、旧 Java 版ソース確認 (§12) により撤回された。粒度は「1 ユニット」:
 *   toolRail と commandCluster が個別にバラバラへ浮くことは無い (`RAIL_PANEL_IDS` の
 *   「leftRail 専用の閉じたユニット」不変条件は維持し、崩すのは「フロート不可」の 1 点だけ)。
 *
 * 「⇤ドック」の戻り先 (S2 §11 改訂、consult 2026-08-01): 当初「フロート化した瞬間に居た
 * region」を state に記憶する案 (`floatOrigin`) を実装したが、**位置ベース + 方向ラベル**へ
 * 差し替えて撤回した (= 保持 state ゼロ、永続化/後方互換の追加負担なし、現在位置から都度
 * `floatPanelSide` + `edgeSnapRegion` で導出する。ModernLayout 側の関心事)。leftRail にも
 * 同じ考え方を適用する (= 左に居れば `leftOuter`、右に居れば `rightOuter` の位置ベース)。
 *
 * 不変条件 (validateWorkspace):
 *   - 全 PanelId が「いずれかの region の panels 配列」または「floating」または (rail パネルの
 *     場合) 「leftRail が railFloating」のどちらか一方にちょうど 1 回だけ現れる (欠落/重複/
 *     両属なし)。
 *   - 'canvas' は必ず 'canvas' region に単独で存在する (§9.7 計測スロットを壊さない)。
 *   - 'canvas' は hidden にできない、floating にもできない。
 *   - leftRail は RAIL_PANEL_IDS のみを含む。RAIL_PANEL_IDS は `floating` に個別には入れない
 *     (= rail パネルのフロートは `railFloating` 経由のみ)。
 *   - leftRail は「`order` に含まれる (docked)」か「`railFloating` が設定されている (floating)」
 *     のどちらか一方 (両方/どちらも無し、は不変条件違反)。
 */
export interface WorkspaceState {
  readonly order: ReadonlyArray<WorkspaceRegion>;
  readonly panels: Readonly<Record<WorkspaceRegion, ReadonlyArray<PanelId>>>;
  /** 非表示パネル集合 (省略エントリ = 表示)。 */
  readonly hidden?: Readonly<Partial<Record<PanelId, boolean>>>;
  /** region ごとのドック幅 px (省略 = レイアウト既定幅)。 */
  readonly widths?: Readonly<Partial<Record<WorkspaceRegion, number>>>;
  /** フロート中パネル id → 画面位置 (省略/キー無し = docked、S2)。rail パネルは含まない。 */
  readonly floating?: Readonly<Partial<Record<PanelId, FloatPosition>>>;
  /**
   * leftRail (toolRail+commandCluster 一体) がフロート中の画面位置 (§12)。未設定 = docked
   * (= `order` に `'leftRail'` が含まれる)。設定時は `order` から `'leftRail'` が除かれる。
   */
  readonly railFloating?: FloatPosition;
}

/** ドック幅の既定値とクランプ範囲 (= 狭過ぎ/広過ぎを防ぐ)。aside 単位で適用。 */
export const TOOL_DOCK_WIDTH_DEFAULT = 244;
export const RIGHT_DOCK_WIDTH_DEFAULT = 260;
export const DOCK_WIDTH_MIN = 180;
export const DOCK_WIDTH_MAX = 420;

/** 幅 px を `[DOCK_WIDTH_MIN, DOCK_WIDTH_MAX]` に閉じ込める (NaN は既定にフォールバック)。 */
export const clampDockWidth = (n: number, fallback: number): number => {
  if (!Number.isFinite(n)) return fallback;
  if (n < DOCK_WIDTH_MIN) return DOCK_WIDTH_MIN;
  if (n > DOCK_WIDTH_MAX) return DOCK_WIDTH_MAX;
  return n;
};

/**
 * 既定レイアウト (= 現状 ModernLayout の標準配置)。表示/幅は省略 (= 既定)。
 * 「標準」preset の素体でもある。
 */
export const DEFAULT_WORKSPACE: WorkspaceState = {
  order: ['leftRail', 'toolColumn', 'canvas', 'rightDock'],
  panels: {
    leftRail: ['toolRail', 'commandCluster'],
    // brushPanel (常設「ブラシ」パネル、modern-brush-panel-2026-08-17.md §4) は
    // toolColumn の最上段 (= ツールプロパティの上、ユーザー確定の既定配置)。
    toolColumn: ['brushPanel', 'toolProperties', 'colorPanel', 'colorPalette'],
    canvas: ['canvas'],
    rightDock: ['previewPanel', 'layerPanel', 'socialDock'],
  },
};

/**
 * パネル id → `DEFAULT_WORKSPACE` 上のホーム region (S2 floating-panels-2026-08-01.md §9
 * 導線5「フロート中のパネルには『ドックへ戻す』導線を用意する」の既定復帰先)。
 * `toolRail`/`commandCluster` (leftRail 専用ユニット) と `canvas` はフロート不可なので
 * 呼び出し側 (= フロート中パネルの一覧) には現れない前提だが、防御的に `DEFAULT_WORKSPACE`
 * を検索して見つかった region を返す (見つからなければ `toolColumn` へフォールバック、
 * 到達しないはずの防御)。
 */
export const homeRegionOf = (id: PanelId): WorkspaceRegion => {
  for (const region of DEFAULT_WORKSPACE.order) {
    if (DEFAULT_WORKSPACE.panels[region].includes(id)) return region;
  }
  return 'toolColumn';
};

/**
 * エッジスナップの着地先 region を決定する (S2 §11「着地先 region の決定: order を canvas で
 * 二分し、ポインタ側のグループから rail でない region を選ぶ」)。`splitAroundCanvas` で得た
 * 左右いずれかのグループから、最初の非レール region (= wide パネルを受け入れられる列) を
 * 返す。現行の 4-region モデルでは `toolColumn`/`rightDock` は常に canvas の異なる側に居る
 * (mirror は order 全体を反転するだけなので、この相対関係は保たれる) ため、両側とも必ず
 * ちょうど 1 つの非レール region を持つ。見つからない (= 到達しないはずの防御、将来 region
 * 構成が増えたときの安全弁) 場合は `null`。
 */
export const edgeSnapRegion = (
  state: WorkspaceState,
  side: 'left' | 'right',
): WorkspaceRegion | null => {
  const { left, right } = splitAroundCanvas(state);
  const group = side === 'left' ? left : right;
  return group.find((r) => r !== 'leftRail') ?? null;
};

/**
 * 左右ミラー変換 (§9.10「UI左右入替」)。**order を反転**する純変換。`panels` / `hidden`
 * / `widths` (中身) は不変 = 左右の列だけが入れ替わる。canvas は order 上で対称な位置
 * なので反転しても中央に残る。
 *
 * これは **レイアウト左右反転** であって canvas-flip (描画ミラー) ではない (上記注記)。
 */
export const mirrorWorkspace = (state: WorkspaceState): WorkspaceState => ({
  ...state,
  order: [...state.order].reverse(),
});

// ── Tier 1 組込 curated preset (§9.10 決定3 / candidate 表) ──────────────────
//
// curate 規律 (§9.10「数で勝負しない」): 各 preset は「誰の・どの使い方」に 1:1 で答える。
// 増やさない。verb 名 (「UI左右入替」) は preset 化で `左右入替` (= 旧 mirror) に吸収される。

/** Tier 1 組込 preset の安定 id (= 永続化キー・cycle 巡回対象)。 */
export type WorkspacePresetId =
  /** 既定・万人向け (現行配置)。 */
  | 'standard'
  /** 左右入替 (= 旧「UI左右入替」を吸収。左右ミラー)。id `mirror` は据え置き。 */
  | 'mirror'
  /** お絵描き集中: 右ドック畳み・ツール最小 (= キャンバス最大)。 */
  | 'focus'
  /** チャット重視: チャット広め・描画 chrome 最小。 */
  | 'chat';

/** preset の表示メタ (ピッカー UI / cycle 用)。purpose 名で統一 (§9.10 命名節)。 */
export interface WorkspacePresetMeta {
  readonly id: WorkspacePresetId;
  /** ピッカー表示名 (purpose 名)。 */
  readonly label: string;
  /** 補助説明 (title / tooltip)。 */
  readonly description: string;
}

/** preset 定義 = メタ + state を生成する純関数 (DEFAULT_WORKSPACE からの変換)。 */
interface WorkspacePresetDef extends WorkspacePresetMeta {
  readonly build: () => WorkspaceState;
}

/**
 * Tier 1 preset レジストリ。**curate 済の 4 つだけ** (§9.10 preset 確定表 2026-06-17)。
 * 巡回順 (cycle) もこの宣言順に従う。各 preset は DEFAULT_WORKSPACE を素体に
 * 表示/畳み/幅/ミラーの組合せを 1 つに束ねたもの (= 同一 state のスナップショット)。
 */
const PRESET_DEFS: ReadonlyArray<WorkspacePresetDef> = [
  {
    id: 'standard',
    label: '標準',
    description: '既定の配置 (万人向け)',
    build: () => DEFAULT_WORKSPACE,
  },
  {
    id: 'mirror',
    label: '左右入替',
    description: 'パネルを左右入れ替え (左利き / 利き手切替)',
    // 旧「UI左右入替」トグルを吸収。order 反転のみ (中身不変)。
    build: () => mirrorWorkspace(DEFAULT_WORKSPACE),
  },
  {
    id: 'focus',
    label: 'お絵描き集中',
    description: 'キャンバス最大 (右ドック畳み・ツール最小)',
    // 右ドック (プレビュー/レイヤー/社交) を畳み、ツールカラムの最近色も畳んで chrome を
    // 最小化 → canvas が両側の空いた分まで広がる。ツール帯/プロパティ/カラーは残す。
    build: () => ({
      ...DEFAULT_WORKSPACE,
      hidden: { previewPanel: true, layerPanel: true, socialDock: true, colorPalette: true },
    }),
  },
  {
    id: 'chat',
    label: 'チャット重視',
    description: 'チャット広め (描画 chrome 最小)',
    // 描画系の重い chrome (プレビュー/レイヤー/コマンド/最近色) を畳み、右ドックを広げて
    // チャット (socialDock) に縦も横も割り当てる。ツール帯/プロパティ/カラーは最小限残す。
    build: () => ({
      ...DEFAULT_WORKSPACE,
      hidden: { previewPanel: true, layerPanel: true, commandCluster: true, colorPalette: true },
      widths: { rightDock: 320 },
    }),
  },
];

/** ピッカー UI / cycle が使う preset メタ一覧 (宣言順 = 巡回順)。 */
export const WORKSPACE_PRESETS: ReadonlyArray<WorkspacePresetMeta> = PRESET_DEFS.map(
  ({ id, label, description }) => ({ id, label, description }),
);

/** 既定 preset id (= 「標準」)。 */
export const DEFAULT_PRESET_ID: WorkspacePresetId = 'standard';

/** 任意文字列が有効な preset id か (= 永続化の前方互換ガード)。 */
export const isWorkspacePresetId = (v: unknown): v is WorkspacePresetId =>
  typeof v === 'string' && PRESET_DEFS.some((p) => p.id === v);

/**
 * preset id から表示用 `WorkspaceState` を導出する (= preset 適用の単一経路、§9.10 決定3)。
 * 未知 id は「標準」にフォールバック (= 前方互換)。
 */
export const resolveWorkspace = (presetId: WorkspacePresetId): WorkspaceState => {
  // 未知 id は「標準」= DEFAULT_WORKSPACE にフォールバック (= 前方互換、非空配列前提に
  // 頼らず明示)。
  const def = PRESET_DEFS.find((p) => p.id === presetId);
  return def ? def.build() : DEFAULT_WORKSPACE;
};

/**
 * 巡回 (§9.10 決定4 `layout.cycle-preset`): 現 preset id の次を返す (末尾→先頭で wrap)。
 * 巡回対象は Tier 1 preset 群 (WORKSPACE_PRESETS の宣言順)。未知 id は先頭の次 (= 2 番目)
 * でなく **先頭** を返す (= 未知から押すとまず「標準」へ正規化される直感)。
 */
export const nextPresetId = (current: WorkspacePresetId): WorkspacePresetId => {
  const idx = PRESET_DEFS.findIndex((p) => p.id === current);
  if (idx < 0) return DEFAULT_PRESET_ID;
  const next = PRESET_DEFS[(idx + 1) % PRESET_DEFS.length];
  // 剰余で必ず範囲内 (next は常に存在) だが noUncheckedIndexedAccess 用に明示。
  return next ? next.id : DEFAULT_PRESET_ID;
};

/**
 * パネルが表示か (= hidden に true で入っていない)。canvas は常に表示。
 */
export const isPanelVisible = (state: WorkspaceState, id: PanelId): boolean => {
  if (id === 'canvas') return true;
  return state.hidden?.[id] !== true;
};

/**
 * region が表示すべきパネル (= hidden を除いた id 列) を返す。順序は panels のまま。
 */
export const visiblePanels = (
  state: WorkspaceState,
  region: WorkspaceRegion,
): ReadonlyArray<PanelId> => state.panels[region].filter((id) => isPanelVisible(state, id));

/**
 * order を canvas を境に「左 aside に入る region 群」「右 aside に入る region 群」へ
 * 分割する (= ModernLayout の 2-aside 構造へマッピングするヘルパ)。canvas 自身は
 * どちらにも含めない (= 中央の独立スロット)。
 */
export const splitAroundCanvas = (
  state: WorkspaceState,
): { left: ReadonlyArray<WorkspaceRegion>; right: ReadonlyArray<WorkspaceRegion> } => {
  const idx = state.order.indexOf('canvas');
  if (idx < 0) {
    // 不変条件違反 (canvas 欠落) の防御: 全部左に寄せて canvas は別途中央に出す。
    return { left: state.order.filter((r) => r !== 'canvas'), right: [] };
  }
  return {
    left: state.order.slice(0, idx),
    right: state.order.slice(idx + 1),
  };
};

/**
 * leftRail (端ドック専用レール) の hug 幅の近似値 (px、実測 ~52px)。§12
 * (floating-panels-2026-08-01.md、追加依頼 2026-08-01) で leftRail が一体フロートできる
 * ようになったため、`asideWidthPx` がフロート中は aside 幅からこの分を差し引いて
 * canvas へ返す (= §3 collapse ルール「フロート中のパネルは場所を予約しない」を
 * leftRail にも適用する)。
 */
export const RAIL_HUG_WIDTH_PX = 52;

/**
 * aside (= canvas で分割した region 群) の幅 px を決める。
 *   - rightDock を含む群 → widths.rightDock ?? 既定 260。
 *   - それ以外 (ツール群) → widths.toolColumn ?? 既定 244。
 * widths は state から拾い clamp する (= 不正値で壊れない)。aside 群が「全パネル非表示」
 * のときは呼び出し側 (ModernLayout) が aside 自体を出さないので幅は問われない。
 *
 * §12: leftRail が**フロート中**のときは、ツール群 (非 rightDock) の既定幅から
 * `RAIL_HUG_WIDTH_PX` を差し引く (= canvas が自動で広がる)。leftRail が**docked**の
 * まま 4 スロットの都合でこの aside に居ない場合 (= rightInner/rightOuter で
 * rightDock 側に同居している場合) は既存どおり差し引かない (= 第5スライスの
 * 既存挙動を変えない、pre-existing の「rightDock 側で rail と同居すると 260px 予算を
 * 分け合う」quirk は本件のスコープ外)。差し引く先をツール群 (非 rightDock 側) に
 * 固定するのは簡略化 (= leftRail は既定で toolColumn と対のため、rightOuter/
 * rightInner に動かした直後にフロートする稀なケースでは「空いた側」の見た目が
 * 厳密ではないが、canvas が広がるという中核要件は満たす)。
 */
export const asideWidthPx = (
  state: WorkspaceState,
  regions: ReadonlyArray<WorkspaceRegion>,
): number => {
  if (regions.includes('rightDock')) {
    return clampDockWidth(
      state.widths?.rightDock ?? RIGHT_DOCK_WIDTH_DEFAULT,
      RIGHT_DOCK_WIDTH_DEFAULT,
    );
  }
  const base = clampDockWidth(
    state.widths?.toolColumn ?? TOOL_DOCK_WIDTH_DEFAULT,
    TOOL_DOCK_WIDTH_DEFAULT,
  );
  if (isRailFloating(state)) {
    return Math.max(DOCK_WIDTH_MIN, base - RAIL_HUG_WIDTH_PX);
  }
  return base;
};

/**
 * aside 群が「leftRail (端ドック専用レール) しか表示パネルを持たない」か (= スリム hug で
 * 描く aside か)。第5スライス (レール端ドック)。
 *
 * 背景: leftRail は renderRegion で常に shrink-0 (hug) なので、toolColumn / rightDock と
 * 同居する aside では leftRail は細いまま (= 同居 region が固定幅を埋める)。しかし leftRail
 * **だけ** が aside の表示内容になると (= 同居 region が全て hidden、あるいはレールが単独で
 * 端へ来た構成)、固定ドック幅 (244/260px) を与えると太い空箱になる → このときは aside を
 * **hug 幅** (= レール内容幅) で描き、レールがスリムのまま端に立つようにする。
 *
 * 判定は **表示パネル基準** (= hidden を除いた実内容): 群の中で leftRail だけが表示パネルを
 * 持ち、他 region が全て空 (= 全 hidden / 無い) なら true。leftRail と toolColumn / rightDock
 * の両方に表示パネルが在る通常配置では false (= 従来の固定/可変ドック幅)。
 */
export const isRailOnlyAside = (
  state: WorkspaceState,
  regions: ReadonlyArray<WorkspaceRegion>,
): boolean => {
  if (!regions.includes('leftRail')) return false;
  if (visiblePanels(state, 'leftRail').length === 0) return false;
  // leftRail 以外の region が表示パネルを 1 つも持たない (= レールだけが見える)。
  return regions.filter((r) => r !== 'leftRail').every((r) => visiblePanels(state, r).length === 0);
};

/**
 * aside 群が「表示すべきパネルを 1 つも持たない」か (= aside 自体を出さない判定)。
 * canvas region はここに来ない (splitAroundCanvas が除外) ので、全パネル非表示なら true。
 */
export const isAsideEmpty = (
  state: WorkspaceState,
  regions: ReadonlyArray<WorkspaceRegion>,
): boolean => regions.every((region) => visiblePanels(state, region).length === 0);

// ── 第3スライス (consult 2026-06-14 決定2「手動ドラッグ = state の直接編集」) ──────
//
// preset を「出発点」とし、その上でユーザーが配置を直接いじる純変換群。今スライスは
// **手動の表示/畳み + ドック幅** まで (= DnD 並べ替えは次スライス、§9.10 残課題)。
// いずれも WorkspaceState を入力に新しい WorkspaceState を返す純関数 (元を破壊しない)。
// 手動編集が入った state は「カスタム」(= 名前付き preset から乖離) として live state に
// 載る (ModernLayout 側でモデル化)。

/**
 * パネルの表示/非表示を切り替えた新 state を返す (= `hidden` フラグの直接編集)。
 * - `canvas` は常に表示なので hide 要求 (visible=false) を無視する (§9.7 死守)。
 * - visible=true のときは `hidden` から該当キーを **削除** (= undefined = 表示) して
 *   余計なフラグを残さない (= clean な state を保つ)。
 * - 結果 `hidden` が空オブジェクトになる場合は `hidden` 自体を省く (= 既定形に正規化)。
 */
export const setPanelHidden = (
  state: WorkspaceState,
  id: PanelId,
  hidden: boolean,
): WorkspaceState => {
  // canvas は隠せない (= 計測スロットを壊さない)。hide 要求は no-op。
  if (id === 'canvas' && hidden) return state;
  const nextHidden: Partial<Record<PanelId, boolean>> = { ...state.hidden };
  if (hidden) {
    nextHidden[id] = true;
  } else {
    delete nextHidden[id];
  }
  // 空なら hidden ごと省く (= DEFAULT 同形に正規化、preset 比較を素直にする)。
  if (Object.keys(nextHidden).length === 0) {
    const { hidden: _omit, ...rest } = state;
    return rest;
  }
  return { ...state, hidden: nextHidden };
};

/**
 * aside (= canvas で分割した region 群) に対応する width キーを返す。
 * rightDock を含む群 → 'rightDock' / それ以外 (ツール群) → 'toolColumn'。
 * asideWidthPx と同じ分岐 (= 幅の読み書きを 1 つの key 規則に集約)。
 */
export const asideWidthKey = (regions: ReadonlyArray<WorkspaceRegion>): WorkspaceRegion =>
  regions.includes('rightDock') ? 'rightDock' : 'toolColumn';

/**
 * aside のドック幅を設定した新 state を返す (= `widths` の直接編集、clamp 適用)。
 * 書き込み先 key は asideWidthKey の規則に従う。値は clampDockWidth で範囲内に丸める。
 */
export const setAsideWidth = (
  state: WorkspaceState,
  regions: ReadonlyArray<WorkspaceRegion>,
  widthPx: number,
): WorkspaceState => {
  const key = asideWidthKey(regions);
  const fallback = key === 'rightDock' ? RIGHT_DOCK_WIDTH_DEFAULT : TOOL_DOCK_WIDTH_DEFAULT;
  const clamped = clampDockWidth(widthPx, fallback);
  return { ...state, widths: { ...state.widths, [key]: clamped } };
};

/**
 * splitter ドラッグの pointer X から aside の新しいドック幅 px を算出する純関数。
 *
 * - **左 aside** (`side='left'`): aside は canvas の左 = splitter は aside 右端。
 *   幅 = pointerX - asideLeftX (右へドラッグ = 広がる)。
 * - **右 aside** (`side='right'`): aside は canvas の右 = splitter は aside 左端。
 *   幅 = asideRightX - pointerX (左へドラッグ = 広がる)。
 *
 * 結果は clampDockWidth で `[DOCK_WIDTH_MIN, DOCK_WIDTH_MAX]` に丸める。
 * (modernSocialDockResize の memberHeightFromPointer と同流儀の幾何純関数。)
 */
export const dockWidthFromPointer = (
  side: 'left' | 'right',
  pointerClientX: number,
  asideEdgeX: number,
  fallbackPx: number,
): number => {
  const raw = side === 'left' ? pointerClientX - asideEdgeX : asideEdgeX - pointerClientX;
  return clampDockWidth(raw, fallbackPx);
};

// ── 第4スライス (consult §9.10 決定1 C: 「ドック内並べ替え + パネル間移動(ドラッグ)」) ──
//
// ユーザーがパネルをドラッグして (a) 同一 region 内で並べ替え、(b) 別 region へ移動できる
// ようにする純変換。書き込み先は `panels[region]` の並び (= 要素を抜いて差し込む)。
// 編集が入った state は ModernLayout 側で custom 化され `{workspace}` 永続に乗る
// (= 既存の表示/幅編集と同じ経路、新永続キーは作らない)。
//
// canvas 不可侵 (= §9.7 計測スロット死守): canvas パネルは動かせない & canvas region は
// ドロップ先にできない。movePanel はこの 2 つを no-op で弾き、結果は常に不変条件を満たす。

/**
 * パネルを目的 region の目的 index へ移動した新 state を返す (= ドラッグ並べ替え/移動の
 * 単一純関数)。同一 region 内なら並べ替え、別 region なら region 間移動になる。
 *
 * - `panelId`: 動かすパネル。`'canvas'` は **動かせない** (no-op、§9.7 死守)。
 * - `toRegion`: 移動先 region。`'canvas'` は **ドロップ先にできない** (no-op、§9.7 死守)。
 *   既に canvas region には canvas しか居ないので、ここへ他パネルを入れない。
 * - `toIndex`: 移動先 region 内での挿入位置 (0..len)。範囲外は端にクランプ。
 *
 * 実装: まず全 region から panelId を取り除き、次に toRegion の (除去後インデックス基準で
 * クランプした) toIndex へ差し込む。同一 region 内移動でも「除去 → 挿入」で一貫する
 * (= 元 index より後ろへ落とすときのオフ・バイ・ワンを `toIndex` の意味論「除去後の配列
 * における挿入位置」に固定して避ける)。
 *
 * 未知 panelId / toRegion はそのまま no-op (= 防御)。元 state は破壊しない。
 */
export const movePanel = (
  state: WorkspaceState,
  panelId: PanelId,
  toRegion: WorkspaceRegion,
  toIndex: number,
): WorkspaceState => {
  // canvas は動かさない / canvas region には入れない (= §9.7 計測スロット死守)。
  if (panelId === 'canvas' || toRegion === 'canvas') return state;
  // leftRail はレール専用の閉じたユニット (= toolRail/commandCluster のみ、第5スライス)。
  // wide パネル (= 非レールパネル) を leftRail へ入れない (= no-op、canvas と同型の防御的
  // ガード): スリム hug 幅前提の leftRail に wide パネルが挿入されてレイアウトが破綻するのを
  // 純関数レベルで弾く。ドロップ判定 (ハンドラ) でも弾くが、ここでも二重に死守する。
  if (toRegion === 'leftRail' && !isRailPanel(panelId)) return state;
  // 未知 region は弾く (= panels に無いキーへ書かない)。
  if (!(toRegion in state.panels)) return state;
  // 対象パネルが本当にどこかに居るか (= docked のどこかの region、または floating。不正 id は
  // no-op)。S2: フロート中パネルの re-dock もこの関数が担う (= movePanel が唯一の「ドックへ
  // 収める」経路。floating 側からの移動も docked 間の並べ替えも同一関数)。
  const dockedRegion = state.order.find((region) => state.panels[region].includes(panelId));
  const wasFloating = isPanelFloating(state, panelId);
  if (!dockedRegion && !wasFloating) return state;

  // 全 region から panelId を除去した panels を作る (= 1 箇所からしか消えない前提。フロート
  // 中だった場合はどの region にも入っていないので除去は no-op)。
  const stripped: Record<WorkspaceRegion, PanelId[]> = {
    leftRail: [...state.panels.leftRail],
    toolColumn: [...state.panels.toolColumn],
    canvas: [...state.panels.canvas],
    rightDock: [...state.panels.rightDock],
  };
  for (const region of state.order) {
    stripped[region] = stripped[region].filter((id) => id !== panelId);
  }
  // 除去後の toRegion 配列に対し index をクランプして差し込む。
  const dest = stripped[toRegion];
  const clampedIndex = Math.max(0, Math.min(Math.trunc(toIndex), dest.length));
  dest.splice(clampedIndex, 0, panelId);

  // re-dock (= フロート中だった) なら floating エントリを消す (= docked と floating の排他を保つ)。
  const base = wasFloating ? withoutFloating(state, panelId) : state;
  return { ...base, panels: stripped };
};

// ── S2 (floating-panels-2026-08-01.md §6 決定4「docked(region,index) | floating(x,y)」) ──
//
// パネルをドックから切り離してフロート配置にする / 既にフロート中のパネルを新しい座標へ
// 移動する。detach と reposition は同じ「floating[panelId] を上書きする」操作なので 1 関数
// (floatPanel) に統合する。re-dock (floating → docked) は上の movePanel が担う (= ドックへ
// 収める経路は movePanel 1 本、フロートへ出す経路は floatPanel 1 本、という対称な 2 関数)。

/** パネルが現在フロート中か (= `floating` にキーを持つか)。 */
export const isPanelFloating = (state: WorkspaceState, id: PanelId): boolean =>
  state.floating?.[id] !== undefined;

/**
 * `floating` から 1 パネル分のエントリを取り除いた新 state を返す (= re-dock 時の後始末)。
 * 結果 `floating` が空オブジェクトになる場合は `floating` キー自体を省く (= setPanelHidden と
 * 同じ「空なら省いて既定形に正規化する」流儀)。対象キーが無ければ元の state をそのまま返す
 * (= 無駄な複製をしない)。
 */
const withoutFloating = (state: WorkspaceState, id: PanelId): WorkspaceState => {
  if (state.floating?.[id] === undefined) return state;
  const nextFloating: Partial<Record<PanelId, FloatPosition>> = { ...state.floating };
  delete nextFloating[id];
  if (Object.keys(nextFloating).length === 0) {
    const { floating: _omit, ...rest } = state;
    return rest;
  }
  return { ...state, floating: nextFloating };
};

/**
 * パネルをフロートへ切り離す (docked → floating) / 既にフロート中のパネルを新しい座標へ
 * 動かす (floating → floating、drag 中の移動確定) の単一関数。
 *
 * - `canvas` / `RAIL_PANEL_IDS` (toolRail・commandCluster) はフロート不可 (no-op、§5 スコープ)。
 * - 未知 panelId (docked にも floating にも存在しない) は no-op。
 * - docked だった場合は所属 region の panels 配列から取り除く。既にフロート中だった場合は
 *   panels には触れず (= 元々どこにも入っていない) 位置だけ上書きする。
 * - `hidden` は変更しない (= フロートへ出しても表示状態は引き継ぐ。非表示パネルをフロートへ
 *   出す操作 (= 将来 UI 拡張の余地) も位置だけ記録して no-op にしない)。
 */
export const floatPanel = (
  state: WorkspaceState,
  panelId: PanelId,
  position: FloatPosition,
): WorkspaceState => {
  if (panelId === 'canvas' || isRailPanel(panelId)) return state;
  const dockedRegion = state.order.find((region) => state.panels[region].includes(panelId));
  const wasFloating = isPanelFloating(state, panelId);
  if (!dockedRegion && !wasFloating) return state;

  const nextPanels = dockedRegion
    ? {
        ...state.panels,
        [dockedRegion]: state.panels[dockedRegion].filter((id) => id !== panelId),
      }
    : state.panels;

  return {
    ...state,
    panels: nextPanels,
    floating: { ...state.floating, [panelId]: position },
  };
};

// ── 第5スライス (consult 2026-06-17「レール(toolRail+commandCluster)の端ドック」) ──────
//
// leftRail (= toolRail + commandCluster の細アイコン列) を **1ユニットの“端ドック専用レール”**
// として、canvas を挟む 4 つの隙間 (スロット) のどこへでも DnD 移動できるようにする純変換。
// panel 移動 (movePanel) でなく **region 移動** = `order` 内の leftRail の位置だけを変える。
//
// **toolRail / commandCluster を wide dock (toolColumn / rightDock) へ panel として入れて
// 太いブロック化する経路は塞ぐ** (= これらは panel-DnD の対象外。movePanel/ドラッグシェルで
// 掴ませない)。レールは region 単位でのみ動き、常に縦長スリム (hug 幅) を保つ。
//
// ## 4 スロット (§9.10 確定表)
//   canvas を挟んで leftRail を置ける位置は、leftRail を除いた 3 region 列
//   `[toolColumn, canvas, rightDock]` の 4 つの隙間 (gap):
//
//   | スロット      | gap | order |
//   |---|---|---|
//   | `leftOuter`   | 0   | `[leftRail, toolColumn, canvas, rightDock]` (既定) |
//   | `leftInner`   | 1   | `[toolColumn, leftRail, canvas, rightDock]` |
//   | `rightInner`  | 2   | `[toolColumn, canvas, leftRail, rightDock]` |
//   | `rightOuter`  | 3   | `[toolColumn, canvas, rightDock, leftRail]` |
//
//   canvas は常に中央 (gap 1/2 は canvas の左右隣)。splitAroundCanvas が order を canvas で
//   左右群に分割するので、どのスロットでも leftRail は canvas を挟んだ左群/右群のどこかに入る。
//   leftRail を除いた残り 3 region の相対並びはそのまま保つ (= mirror preset 等とも合成可能)。

/** leftRail (端ドック専用レール) を置ける 4 スロット (canvas を挟む 4 隙間)。 */
export type RailSlot = 'leftOuter' | 'leftInner' | 'rightInner' | 'rightOuter';

/** 4 スロットの宣言順 (= UI のドロップインジケータ列・gap index 0..3 と一致)。 */
export const RAIL_SLOTS: ReadonlyArray<RailSlot> = [
  'leftOuter',
  'leftInner',
  'rightInner',
  'rightOuter',
];

/** RailSlot → 残り 3 region 列に対する gap index (0..3)。 */
const RAIL_SLOT_GAP: Readonly<Record<RailSlot, number>> = {
  leftOuter: 0,
  leftInner: 1,
  rightInner: 2,
  rightOuter: 3,
};

/**
 * 現 order における leftRail の所在スロットを返す (= ドロップ前のハイライト・現在地表示用)。
 * leftRail を除いた残り region 列の中で、leftRail が何番目の gap に居たかを gap index で
 * 求め、対応する RailSlot を返す。order に leftRail が無い (不変条件違反) なら既定の leftOuter。
 */
export const currentRailSlot = (state: WorkspaceState): RailSlot => {
  const railIdx = state.order.indexOf('leftRail');
  if (railIdx < 0) return 'leftOuter';
  // leftRail より前に在る「leftRail 以外の region」数 = 除去後配列での gap index。
  const gap = state.order.slice(0, railIdx).filter((r) => r !== 'leftRail').length;
  const slot = RAIL_SLOTS[Math.min(gap, RAIL_SLOTS.length - 1)];
  return slot ?? 'leftOuter';
};

/**
 * leftRail region を指定スロットへ移した新 order を持つ state を返す (= region 移動の単一純関数)。
 *
 * - leftRail を order から取り除き、残り region 列 (相対並び保持) の指定 gap へ差し込む。
 * - canvas は残り列の中にそのまま残る (= 不可侵・中央維持。gap 1/2 は canvas の左右隣)。
 * - panels / hidden / widths は不変 (= region の位置だけ動かす。中身・スリム属性は保つ)。
 * - leftRail が order にも無く railFloating でもない (不変条件違反) なら no-op (同一参照)。
 *
 * §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail が**フロート中
 * だった場合の re-dock もこの関数が担う** (= `movePanel` が通常パネルの re-dock も
 * 同一関数で担うのと対称な設計)。フロート中だった場合は `railFloating` を消し、
 * 指定スロットへ挿入する。
 *
 * gap index の意味論: 除去後の残り列に対する挿入位置 (0 = 先頭, len = 末尾)。残り列は常に
 * 3 要素 (toolColumn / canvas / rightDock) なので gap は 0..3 = ちょうど 4 スロット。
 */
export const moveRail = (state: WorkspaceState, slot: RailSlot): WorkspaceState => {
  const wasFloating = isRailFloating(state);
  if (!state.order.includes('leftRail') && !wasFloating) return state;
  const rest = state.order.filter((r) => r !== 'leftRail');
  const gap = Math.max(0, Math.min(RAIL_SLOT_GAP[slot], rest.length));
  const nextOrder = [...rest.slice(0, gap), 'leftRail' as WorkspaceRegion, ...rest.slice(gap)];
  const base = wasFloating ? withoutRailFloating(state) : state;
  return { ...base, order: nextOrder };
};

// ── §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail の一体フロート ──
//
// 旧 Java 版ソース (`OekakichatClientPanel.openToolFrame()`) 確認により、Modern の
// toolRail/commandCluster も (Classic のサイドバー全体と同じく) フロート可能にする。
// 粒度は「1 ユニット」: toolRail と commandCluster が個別にバラバラへ浮くことは無い
// (RAIL_PANEL_IDS の「leftRail 専用の閉じたユニット」不変条件は維持、崩すのは
// 「フロート不可」の 1 点だけ)。detach/reposition/re-dock は通常パネルの
// floatPanel/movePanel と対称な floatRail/moveRail の 2 関数で表す。

/** leftRail が現在フロート中か (= `railFloating` にキーを持つか)。 */
export const isRailFloating = (state: WorkspaceState): boolean => state.railFloating !== undefined;

/**
 * `railFloating` を取り除いた新 state を返す (= re-dock 時の後始末、`withoutFloating` の
 * rail 版)。対象が無ければ元の state をそのまま返す (= 無駄な複製をしない)。
 */
const withoutRailFloating = (state: WorkspaceState): WorkspaceState => {
  if (state.railFloating === undefined) return state;
  const { railFloating: _omit, ...rest } = state;
  return rest;
};

/**
 * leftRail をフロートへ切り離す (docked → floating) / 既にフロート中の leftRail を新しい
 * 座標へ動かす (floating → floating、drag 中の移動確定) の単一関数 (= `floatPanel` の
 * rail 版、`moveRail` と対をなす)。
 *
 * - leftRail が order にも無く railFloating でもない (不変条件違反)場合は no-op。
 * - docked だった場合は order から `'leftRail'` を取り除く。既にフロート中だった場合は
 *   order には触れず (= 元々含まれていない) 位置だけ上書きする。
 * - panels.leftRail (中身) / hidden / widths は不変 (= 位置と docked/floating の別だけ動かす)。
 */
export const floatRail = (state: WorkspaceState, position: FloatPosition): WorkspaceState => {
  const wasFloating = isRailFloating(state);
  if (!state.order.includes('leftRail') && !wasFloating) return state;
  const nextOrder = state.order.filter((r) => r !== 'leftRail');
  return { ...state, order: nextOrder, railFloating: position };
};

// ── バグ修正 (2026-08-01 実機検証、S1 と同じバグクラスの残り) ──────────────────────
//
// 旧 `railSlotFromPointer` は「pointer X → 必ずいずれかの 4 スロットを返す全域関数」
// だった (= row を 4 帯に割って中点で振り分けるだけ、「どのスロットからも遠い」を
// 表現できない)。row の実測矩形はヘッダ帯を除く画面ほぼ全域 (= canvas 中央も含む) な
// ので、**キャンバス中央でリリースしても必ずどれかのスロットに「吸い込まれ」、
// フロート化が実質到達不能**という実害が実機 Playwright 検証で判明した (S1 で
// 「row 外でも古い slot が残る」を row 矩形ガードで塞いだのと同じバグクラスの残り —
// 全域関数である限り「近接」の概念が無く、境界ガードだけでは救えない)。
//
// 修正: 通常パネルの `resolveDockSnapTarget` (= 40px 吸着マージン、DOCK_SNAP_MARGIN_PX)
// と同じ「近接ドッキング」の心的モデルを rail にも適用する。4 スロットの位置
// (= `railSlotIndicatorOffset` が描くガイド線の X 座標と同一のアンカー) それぞれに
// 吸着圏を持たせ、**どのアンカーの吸着圏内にも入っていなければ `null`** (= フロート
// 候補) を返す。マージン値は `DOCK_SNAP_MARGIN_PX` と揃えた (= 「近くに寄せればドック、
// 離せば浮く」という通常パネルと同一の感覚にするため。shared/panelDragGeometry.ts への
// 依存を増やさないよう値は独立定数として複製し、コメントで対応関係を明記する)。

/**
 * レールスロットの吸着マージン (px)。通常パネルの `DOCK_SNAP_MARGIN_PX`
 * (`shared/panelDragGeometry.ts`) と同値に揃える (= 「近くに寄せればドック、離せば
 * 浮く」という通常パネルと同一の心的モデルにするため。workspaceLayout.ts は
 * shared/ に依存しない純粋層に保つ方針のため、値を複製する形で揃える)。
 */
export const RAIL_SLOT_SNAP_MARGIN_PX = 40;

/**
 * レール DnD 中の pointer X から、吸着圏内にある最寄りドロップスロットを判定する純関数。
 * 4 スロットそれぞれの位置は `railSlotIndicatorOffset` が描くガイド線と同じアンカー
 * (`leftOuter`=rowLeft / `leftInner`=canvasLeft / `rightInner`=canvasRight /
 * `rightOuter`=rowRight) にあり、**そこから `marginPx` (既定 `RAIL_SLOT_SNAP_MARGIN_PX`)
 * 以内**ならそのスロットの吸着圏内とみなす。複数のアンカーが吸着圏に重なる場合は
 * 最も近いものを返す (= `resolveDockSnapTarget` と同じ tie-break)。**どの吸着圏にも
 * 入っていなければ `null`** (= フロート候補、呼び出し側が floatPreview へ倒す)。
 *
 * 純粋な幾何関数 (DOM 非依存)。呼び出し側が rect を計測して渡す。
 */
export const railSlotNearPointer = (
  pointerClientX: number,
  rowLeftX: number,
  rowRightX: number,
  canvasLeftX: number,
  canvasRightX: number,
  marginPx: number = RAIL_SLOT_SNAP_MARGIN_PX,
): RailSlot | null => {
  const anchors: ReadonlyArray<{ readonly slot: RailSlot; readonly x: number }> = [
    { slot: 'leftOuter', x: rowLeftX },
    { slot: 'leftInner', x: canvasLeftX },
    { slot: 'rightInner', x: canvasRightX },
    { slot: 'rightOuter', x: rowRightX },
  ];
  let best: RailSlot | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const a of anchors) {
    const dist = Math.abs(pointerClientX - a.x);
    if (dist <= marginPx && dist < bestDist) {
      bestDist = dist;
      best = a.slot;
    }
  }
  return best;
};

/**
 * レール DnD 中の 4 スロット ドロップインジケータ (縦線) の x 位置を、主 row を基準にした
 * **オフセット px** で返す純関数 (= DOM 非依存、呼び出し側が rect を計測して渡す)。
 *
 * バグ修正 (2026-06-17): 内側 2 スロットを「row 中央 ±60px」のハードコードで近似していた
 * ため、内側ガイドが canvas 端でなく canvas 中央付近に出ていた。判定 (railSlotNearPointer)
 * と同一の rect (row / canvas 端) から計算してガイドと判定を一致させる。
 *
 *   - `leftOuter`  = row 左端          → 0
 *   - `leftInner`  = canvas の左端     → canvasLeftX - rowLeftX
 *   - `rightInner` = canvas の右端     → canvasRightX - rowLeftX
 *   - `rightOuter` = row 右端          → rowRightX - rowLeftX
 *
 * いずれも row 左端を原点 (0) としたオフセット (= row を relative anchor に absolute 配置する
 * インジケータへ inline style `left: ${offset}px` で渡す)。canvas 端は実 rect から出すので
 * パネル表示/幅変更/resize でも判定とズレない (= railSlotNearPointer が見る rect と同一ソース)。
 */
export const railSlotIndicatorOffset = (
  slot: RailSlot,
  rowLeftX: number,
  rowRightX: number,
  canvasLeftX: number,
  canvasRightX: number,
): number => {
  switch (slot) {
    case 'leftOuter':
      return 0;
    case 'leftInner':
      return canvasLeftX - rowLeftX;
    case 'rightInner':
      return canvasRightX - rowLeftX;
    case 'rightOuter':
      return rowRightX - rowLeftX;
  }
};

// ── パネル migration (modern-brush-panel-2026-08-17.md §6「★実装上の最大の落とし穴」) ──
//
// 背景: validateWorkspace は「全 PanelId がちょうど 1 箇所に現れる」ことを不変条件にして
// いる (= 意図的に厳格、崩さない)。一方で永続化された custom state は「その JSON が保存
// された時点の ALL_PANEL_IDS」しか知らない。**新しい PanelId を足すたびに**、既存ユーザー
// の手動編集済み custom state は validateWorkspace に「missing panel」で落とされ、
// 呼び出し側 (modernWorkspacePreference.ts) が default preset へフォールバックする
// = 手動配置が全消しになる。
//
// 対策: 永続 state を読む段 (validateWorkspace の**前**) で、**「ALL_PANEL_IDS には居るが
// この state のどこにも居ない」パネルを既定 (= DEFAULT_WORKSPACE) の位置へ注入**してから
// validate する。validateWorkspace 自体は不変条件チェッカのまま変えない (= 本当に壊れた
// state — 型不正・重複・canvas 不変条件違反等 — は従来どおり validateWorkspace が捕まえ、
// 呼び出し側が捨てる)。今後パネルを増やすたびにこの関数がそのまま働く (= 汎用の
// 「欠けを既定位置に補う」段、対症療法にしない)。

/**
 * 「欠けている」を判定するための、パネルが state 上のどこかに現れているかの集合を作る。
 * validateWorkspace の `seen` 構築 (docked / floating / railFloating の 3 経路) と同じ規則
 * を踏襲する。**region 配列だけを見ると、フロート中パネルを欠落と誤判定して二重挿入して
 * しまう**ので、`floating` と `railFloating` も必ず合わせて見る。
 */
const collectPresentPanelIds = (state: WorkspaceState): ReadonlySet<PanelId> => {
  const present = new Set<PanelId>();
  // docked: 4 region いずれかの panels 配列。
  for (const region of DEFAULT_WORKSPACE.order) {
    for (const id of state.panels[region] ?? []) present.add(id);
  }
  // floating: leftRail 一体 (toolRail/commandCluster) がフロート中なら rail パネルは
  // railFloating 側で「居る」扱い (= 通常パネルの `floating` とは別枠、validateWorkspace と
  // 同じ扱い)。
  if (isRailFloating(state)) {
    for (const id of RAIL_PANEL_IDS) present.add(id);
  }
  // floating: 通常パネルの個別フロート。
  for (const id of Object.keys(state.floating ?? {})) present.add(id as PanelId);
  return present;
};

/**
 * `ALL_PANEL_IDS` のうち `state` のどこにも現れていないパネルを、`DEFAULT_WORKSPACE` 上の
 * ホーム region (= `homeRegionOf`) の既定順に沿った位置へ **表示状態で docked 挿入**した
 * 新しい state を返す (= migration 本体、§6 対策)。
 *
 * - **1 bit も変えない**: 欠けが 1 つも無ければ引数をそのまま返す (同一参照)。
 * - **`hidden` にはしない**: 注入したパネルは `hidden` へ触れない (= 省略 = 表示、新パネルが
 *   隠れたまま存在に気付けない事故を避ける)。
 * - **rail パネル (`RAIL_PANEL_IDS`) は必ず leftRail へ**: `homeRegionOf` は
 *   `DEFAULT_WORKSPACE.panels` を検索するだけなので、rail パネルは自然に `leftRail` を返す
 *   (= 不変条件「leftRail は RAIL_PANEL_IDS のみ」を破らない)。
 * - **挿入位置**: 挿入先 region の既存パネルを、`DEFAULT_WORKSPACE` 上でのそのパネルより
 *   前に来る既定隣人の数だけ後ろへずらした位置に置く (= 既定順との相対関係をなるべく
 *   保った「既定位置」)。
 * - 型・構造が壊れている state (= 重複・canvas 不変条件違反等) はここでは直さない。
 *   呼び出し側は本関数の戻り値を必ず `validateWorkspace` に通し、なお不正なら従来どおり
 *   捨てる (= 「欠けは直す／本当に壊れているものは捨てる」の線引きを維持)。
 */
export const repairMissingPanels = (state: WorkspaceState): WorkspaceState => {
  const present = collectPresentPanelIds(state);
  const missing = ALL_PANEL_IDS.filter((id) => !present.has(id));
  if (missing.length === 0) return state;

  let panels: Record<WorkspaceRegion, ReadonlyArray<PanelId>> = { ...state.panels };
  for (const id of missing) {
    const region = homeRegionOf(id);
    const current = panels[region];
    const defaultOrderInRegion = DEFAULT_WORKSPACE.panels[region];
    const idxInDefault = defaultOrderInRegion.indexOf(id);
    // 既定順で自分より前に来る「既に現在の配列に居る」隣人の数 = 挿入位置。
    const insertAt = defaultOrderInRegion
      .slice(0, idxInDefault < 0 ? defaultOrderInRegion.length : idxInDefault)
      .filter((neighborId) => current.includes(neighborId)).length;
    panels = {
      ...panels,
      [region]: [...current.slice(0, insertAt), id, ...current.slice(insertAt)],
    };
  }
  return { ...state, panels };
};

/** WorkspaceState の不変条件を検証する (テスト / dev assert 用)。問題なければ null。 */
export const validateWorkspace = (state: WorkspaceState): string | null => {
  // §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail は「order に
  // 含まれる (docked)」か「railFloating が設定されている (floating)」のどちらか一方。
  // 両方/どちらも無し、は不変条件違反 (= 通常パネルの docked/floating 排他と同じ検査を
  // region レベルでも行う)。
  const railDocked = state.order.includes('leftRail');
  const railFloating = isRailFloating(state);
  if (railDocked && railFloating) return 'leftRail must not be both docked and floating';
  if (!railDocked && !railFloating) return 'leftRail must be either docked or floating';

  const seen = new Set<PanelId>();
  for (const region of state.order) {
    for (const id of state.panels[region]) {
      if (seen.has(id)) return `duplicate panel: ${id}`;
      seen.add(id);
    }
  }
  // §12: leftRail がフロート中のとき、上のループは (order に leftRail が無いため)
  // panels.leftRail の中身 (toolRail/commandCluster) を訪れない。ここで rail パネルを
  // 「1 ユニットとしてフロート中」= 見た扱いにする (通常パネルの individual floating とは
  // 別枠、下の「rail panel must not float」チェックと整合させるため `floating` 側へは
  // 加えない、`seen` にだけ加える)。
  if (railFloating) {
    for (const id of RAIL_PANEL_IDS) {
      if (seen.has(id)) return `duplicate panel: ${id}`;
      seen.add(id);
    }
  }
  // S2 (§6 決定4): フロート中パネルも「1回だけ現れる」対象に加える。docked (panels 配列) と
  // floating は排他なので、既に docked 側で見た id が floating にも重複していれば違反。
  const floatingEntries = Object.entries(state.floating ?? {}) as ReadonlyArray<
    [PanelId, FloatPosition | undefined]
  >;
  for (const [id] of floatingEntries) {
    if (seen.has(id)) return `panel both docked and floating: ${id}`;
    seen.add(id);
  }
  for (const id of ALL_PANEL_IDS) {
    if (!seen.has(id)) return `missing panel: ${id}`;
  }
  if (state.panels.canvas.length !== 1 || state.panels.canvas[0] !== 'canvas') {
    return 'canvas region must contain exactly [canvas]';
  }
  if (state.hidden?.canvas === true) {
    return 'canvas must not be hidden';
  }
  if (state.floating?.canvas !== undefined) {
    return 'canvas must not float';
  }
  // leftRail はレール専用ユニット (= toolRail/commandCluster のみ、第5スライス)。wide パネル
  // が混入していたら不変条件違反 (= canvas 不変条件と並べる防御)。docked/floating に関わらず
  // panels.leftRail の中身は常にこの規則に従う。
  for (const id of state.panels.leftRail) {
    if (!isRailPanel(id)) return `leftRail must contain only rail panels: ${id}`;
  }
  // §12: RAIL_PANEL_IDS (toolRail/commandCluster) は**個別には** `floating` に入れない
  // (= rail パネルのフロートは region 単位の `railFloating` 経由のみ、1 ユニットの粒度を守る)。
  for (const [id] of floatingEntries) {
    if (isRailPanel(id)) return `rail panel must not float individually: ${id}`;
  }
  // S2: フロート位置は有限数値であること (= 永続化からの復元で NaN/Infinity 混入を弾く)。
  for (const [id, pos] of floatingEntries) {
    if (pos === undefined || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) {
      return `invalid float position: ${id}`;
    }
  }
  // §12: railFloating の位置も同じ有限数値チェック。
  if (state.railFloating !== undefined) {
    if (!Number.isFinite(state.railFloating.x) || !Number.isFinite(state.railFloating.y)) {
      return 'invalid float position: leftRail';
    }
  }
  return null;
};
